/**
 * @file schemas/case-application.ts
 * @description 案件「申請明細」schema（cases.application_details jsonb，前後端共用）。
 *
 * 2026-07-04 客戶 PPT S9/S10：新建案件的申請明細按驗證方案分兩套結構——
 *
 * - **有機 TOP**（{@link applicationDetailsTopSchema}，kind='TOP'）：
 *   產品品項（可多個、每項可附件）、產品範圍（可多個、每項可附件）、驗證面積、
 *   驗證場所地址、採收後處理場所地址（自客戶檔案土地 / 採後場所勾選帶入快照）。
 * - **產銷履歷 TAP**（{@link applicationDetailsTapSchema}，kind='TAP'）：
 *   多行表格 {產品類別、產品品項、作物產期、申請面積、合理採收量}（每行可附件）
 *   + 生產地筆數（landCount）+ 場區筆數（siteCount）+ 驗證總面積（各行申請面積自動加總）
 *   + 兩類場所地址快照。（序 56：原「生產地及場區筆數」拆分為生產地筆數 / 場區筆數兩欄。）
 * - ECO 暫無獨立表樣，回落 TOP（見 {@link applicationKindForScheme}）。
 *
 * 面積一律用「最多 4 位小數的公頃數字字串」（如 '1.2345'）承載，避免浮點誤差；
 * 加總用整數萬分位累加（{@link sumTapArea}）。
 *
 * 兼容策略（無 DB 遷移）：
 * - 舊結構（{@link applicationDetailsSchema}，無 kind 判別鍵）繼續合法，
 *   讀取端統一走 {@link parseApplicationDetails}，舊 jsonb 原樣歸 'LEGACY'。
 * - 新 TOP/TAP 結構**繼承**舊結構全部欄位（驗證選項 / 輔導員 / 銷售通路…仍可填），
 *   既有讀取方（獲證名錄同步等讀 salesChannels / verificationOption）不受影響。
 *
 * 場所地址快照：客戶檔案的土地（client_lands）/ 採後處理場所
 * （client_processing_sites）仍是 master 數據；建案時勾選的 id 存
 * selectedLandIds / selectedSiteIds，地址文字拍進 landAddressSnapshot /
 * siteAddressSnapshot（快照不隨客戶檔案後續修改而變）。
 */
import { z } from 'zod';
// ─── 舊版（LEGACY）申請明細：自 schemas/cases.ts 遷入，形狀不變 ────────────────
/**
 * 驗證選項：個別驗證 / 集團驗證。
 * 集團驗證時另填農戶數（groupFarmerCount）。
 */
export const CASE_VERIFICATION_OPTIONS = ['individual', 'group'];
/**
 * 產品銷售對象 / 通路（可複選）。
 * - auction      拍賣市場
 * - supermarket  超市賣場
 * - processor    加工業者（另填 salesProcessorName）
 * - distributor  分裝 / 流通業者（另填 salesDistributorName）
 * - other        其他銷售對象（另填 salesOtherName）
 */
export const SALES_CHANNELS = [
    'auction',
    'supermarket',
    'processor',
    'distributor',
    'other',
];
/**
 * 申請表單明細 schema（舊版 / 通用欄位，落庫於 cases.application_details，jsonb）。
 *
 * 對應紙本「有機農產品驗證申請表」的非系統欄位：驗證選項、輔導員、
 * 驗證面積、產品銷售對象 / 通路。系統既有的「驗證類型 / 驗證方案 / 客戶」
 * 仍由 cases 主表欄位承載，不重複進此明細。
 *
 * ⚠️ 2026-07-04 起本 schema 兼任兩個角色：
 * 1. 存量案件（無 kind 鍵）的讀取 / 寫入契約（LEGACY）；
 * 2. 新 TOP/TAP 判別聯合的**共用基底**（新結構 extend 本 schema，
 *    驗證選項 / 輔導員 / 銷售通路等欄位在新結構下仍可填寫）。
 * 舊三個面積欄（applyAreaHa / organicAreaHa / organicConversionAreaHa）
 * 僅為存量資料保留；新表單改用 TOP.verificationArea / TAP.totalArea（4 位小數字串）。
 */
export const applicationDetailsSchema = z.object({
    /** 驗證選項：個別 / 集團 */
    verificationOption: z.enum(CASE_VERIFICATION_OPTIONS).optional(),
    /** 集團驗證農戶數（僅集團驗證時填寫） */
    groupFarmerCount: z.number().int().positive().max(99999).optional(),
    /** 輔導員姓名 */
    counselorName: z.string().max(50).optional(),
    /** 輔導員手機 */
    counselorPhone: z.string().max(30).optional(),
    /** 申請驗證面積（公頃）— 初次申請 / 增列查驗等（LEGACY 存量欄） */
    applyAreaHa: z.number().nonnegative().max(999999).optional(),
    /** 有機面積（公頃）— 追蹤 / 展延查驗（LEGACY 存量欄） */
    organicAreaHa: z.number().nonnegative().max(999999).optional(),
    /** 有機轉型期面積（公頃）— 追蹤 / 展延查驗（LEGACY 存量欄） */
    organicConversionAreaHa: z.number().nonnegative().max(999999).optional(),
    /** 產品銷售對象 / 通路（可複選） */
    salesChannels: z.array(z.enum(SALES_CHANNELS)).optional(),
    /** 拍賣市場名稱（勾選 auction 時填，序131） */
    salesAuctionName: z.string().max(100).optional(),
    /** 超市賣場名稱（勾選 supermarket 時填，序131） */
    salesSupermarketName: z.string().max(100).optional(),
    /** 加工業者名稱（勾選 processor 時填） */
    salesProcessorName: z.string().max(100).optional(),
    /** 分裝 / 流通業者名稱（勾選 distributor 時填） */
    salesDistributorName: z.string().max(100).optional(),
    /** 其他銷售對象業者名稱（勾選 other 時填） */
    salesOtherName: z.string().max(100).optional(),
});
// ─── 面積工具（4 位小數公頃字串，整數萬分位運算避免浮點） ─────────────────────
/** 面積小數位數（公頃，0.0000 格式） */
export const AREA_DECIMALS = 4;
/** 1 公頃 = 10000 萬分位單位 */
const AREA_UNIT = 10 ** AREA_DECIMALS;
/** 面積字串正則：非負、最多 4 位小數（'12'、'1.2'、'0.0001' 合法；'.5' / '-1' / '1.00001' 不合法） */
const AREA_STRING_RE = /^\d+(\.\d{1,4})?$/;
/**
 * 面積字串（公頃）zod schema：非負、最多 4 位小數、整體長度 ≤ 12
 * （上限約 9,999,999.9999 公頃，遠超業務量級）。
 */
export const areaHaStringSchema = z
    .string()
    .trim()
    .max(12, '面積數字過長')
    .regex(AREA_STRING_RE, '面積須為最多 4 位小數的非負數字（公頃）');
/**
 * 面積字串 → 整數萬分位單位（'1.2345' → 12345）。
 * 非法格式 / 非字串 / 超出安全整數範圍回 null（呼叫端可據此忽略該行）。
 */
export function areaStringToUnits(value) {
    if (typeof value !== 'string')
        return null;
    const m = AREA_STRING_RE.exec(value.trim());
    if (!m)
        return null;
    const intPart = Number(value.trim().split('.')[0]);
    const fracPart = Number((m[1]?.slice(1) ?? '').padEnd(AREA_DECIMALS, '0') || '0');
    const units = intPart * AREA_UNIT + fracPart;
    return Number.isSafeInteger(units) ? units : null;
}
/** 整數萬分位單位 → 面積字串（12345 → '1.2345'；負值 / 非整數輸入夾為 0 / 四捨五入） */
export function unitsToAreaString(units) {
    const u = Math.max(0, Math.round(units));
    const intPart = Math.floor(u / AREA_UNIT);
    const fracPart = String(u % AREA_UNIT).padStart(AREA_DECIMALS, '0');
    return `${intPart}.${fracPart}`;
}
/**
 * 任意輸入正規化為 4 位小數面積字串。
 *
 * - 字串：須符合面積格式，正規化補足 4 位小數（'1.2' → '1.2000'）；
 * - 數字：以萬分位四捨五入（InputNumber 非 stringMode 的兜底路徑）；
 * - 其餘 / 非法 / 負數 → undefined。
 */
export function toAreaString(value) {
    if (typeof value === 'number') {
        if (!Number.isFinite(value) || value < 0)
            return undefined;
        const units = Math.round(value * AREA_UNIT);
        return Number.isSafeInteger(units) ? unitsToAreaString(units) : undefined;
    }
    const units = areaStringToUnits(value);
    return units === null ? undefined : unitsToAreaString(units);
}
/**
 * TAP 各行申請面積加總 → 驗證總面積（4 位小數字串）。
 *
 * 用整數萬分位累加避免浮點誤差（0.1 + 0.2 類問題）；
 * 空陣列 / 全非法行回 '0.0000'，非法行（格式錯 / 缺值）按 0 忽略。
 */
export function sumTapArea(rows) {
    let total = 0;
    for (const row of rows ?? []) {
        total += areaStringToUnits(row?.appliedArea) ?? 0;
    }
    return unitsToAreaString(total);
}
// ─── 新版（TOP / TAP）申請明細判別聯合 ────────────────────────────────────────
/**
 * TOP 產品條目（品項 / 範圍共用形狀）：名稱 + 可選附件（上傳識別用，files 表 ID）。
 * 「附件上傳識別」現階段 = 先上傳存 fileIds；AI 識別自動填行留待後續。
 */
export const caseProductEntrySchema = z.object({
    /**
     * 品項 / 範圍名稱。
     * 2026-09-07 回報序6：驗證範圍改為自由文字輸入（`品項[範圍1、範圍2…]`），
     * 單筆長度放寬至 1000 字（jsonb 無 DB 長度約束）。
     */
    name: z.string().min(1).max(1000),
    /** 附件（files 表 ID 陣列，可選） */
    attachmentFileIds: z.array(z.number().int().positive()).max(50).optional(),
});
/** TAP 申請明細單行：{產品類別、產品品項、作物產期、申請面積、合理採收量} + 可選附件 */
export const tapApplicationRowSchema = z.object({
    /**
     * 作物字典 ID（crops.id）。
     * 新建/修改 TAP 案件由後端校驗啟用狀態，並以字典內容重寫類別與品項文字；
     * optional 僅為相容 2026-07-27 前已落庫、尚未關聯字典的歷史案件。
     */
    cropId: z.number().int().positive().optional(),
    /** 產品類別 */
    productCategory: z.string().min(1).max(100),
    /** 產品品項 */
    productItem: z.string().min(1).max(200),
    /** 作物產期（自由文字，如 "3-6 月"） */
    cropPeriod: z.string().max(100).optional(),
    /**
     * 申請面積（公頃，4 位小數字串）。
     * 2026-09-13 回報序16：產銷履歷申請明細解鎖為「非必填」，面積可留空（加總按 0 計）。
     */
    appliedArea: areaHaStringSchema.optional(),
    /** 合理採收量（自由文字，含單位，如 "12000 公斤"） */
    reasonableYield: z.string().max(100).optional(),
    /** 附件（files 表 ID 陣列，可選，上傳識別用） */
    attachmentFileIds: z.array(z.number().int().positive()).max(50).optional(),
});
/**
 * 場所選定共用欄位（TOP / TAP 皆有）：
 * 從客戶檔案土地 / 採後處理場所勾選帶入——id 供追溯 master、地址文字為建案時快照。
 */
const siteSelectionShape = {
    /** 勾選的客戶土地 id（client_lands.id，master 追溯用） */
    selectedLandIds: z.array(z.number().int().positive()).max(500).optional(),
    /** 勾選的採後處理場所 id（client_processing_sites.id，master 追溯用） */
    selectedSiteIds: z.array(z.number().int().positive()).max(500).optional(),
    /** 驗證場所地址快照（建案當下的土地地址文字） */
    landAddressSnapshot: z.array(z.string().max(500)).max(500).optional(),
    /** 採收後處理場所地址快照（建案當下的場所地址文字） */
    siteAddressSnapshot: z.array(z.string().max(500)).max(500).optional(),
};
/**
 * 有機 TOP 申請明細（kind='TOP'；ECO 暫按 TOP）。
 * 繼承 LEGACY 通用欄位（驗證選項 / 輔導員 / 銷售通路…）。
 */
export const applicationDetailsTopSchema = applicationDetailsSchema.extend({
    kind: z.literal('TOP'),
    /** 產品品項（可多個，每項可附件上傳識別） */
    productItems: z.array(caseProductEntrySchema).max(100).optional(),
    /** 產品範圍（可多個，每項可附件上傳識別） */
    productScopes: z.array(caseProductEntrySchema).max(100).optional(),
    /** 驗證面積（公頃，4 位小數字串） */
    verificationArea: areaHaStringSchema.optional(),
    ...siteSelectionShape,
});
/**
 * 產銷履歷 TAP 申請明細（kind='TAP'）。
 * 繼承 LEGACY 通用欄位；rows 可為空陣列（2026-09-13 回報序16 解鎖非必填，原本至少一行），
 * totalArea 應為各行 appliedArea 加總
 * （服務層寫入前以 {@link normalizeCaseApplicationDetails} 重算，不信任客戶端）。
 */
export const applicationDetailsTapSchema = applicationDetailsSchema.extend({
    kind: z.literal('TAP'),
    /** 申請明細多行：{產品類別、產品品項、作物產期、申請面積、合理採收量} */
    rows: z.array(tapApplicationRowSchema).max(200),
    /**
     * 生產地筆數（客戶紙本「生產地及場區筆數」拆分後的「生產地」項；序 56）。
     * 2026-07-07 新增可空欄；application_details 為 jsonb，無 DB 遷移，直接進 jsonb。
     * 舊資料無此鍵時讀取回落「—」/0。
     */
    landCount: z.number().int().nonnegative().max(99999).optional(),
    /**
     * 場區筆數（客戶紙本「生產地及場區筆數」拆分後的「場區」項；序 56）。
     * ⚠️ 欄位名保留 siteCount 不改名（避免破壞既有資料 / 引用），僅語意收窄為「場區筆數」。
     */
    siteCount: z.number().int().nonnegative().max(99999).optional(),
    /** 驗證總面積（公頃，4 位小數字串 = Σ 各行申請面積） */
    totalArea: areaHaStringSchema,
    ...siteSelectionShape,
});
/**
 * LEGACY 輸入分支：舊結構 + 明確**禁止**帶 kind 鍵。
 *
 * 若不加此約束，union 解析時「kind='TOP' 但 productItems 非法」的 payload
 * 會回落到寬鬆的舊 schema 並**靜默剝掉**新結構欄位；鎖 kind 為 undefined 後，
 * 帶了 kind 的非法 payload 會被整體拒絕（fail loudly），不帶 kind 的舊調用方不受影響。
 */
const applicationDetailsLegacyInputSchema = applicationDetailsSchema.extend({
    kind: z.undefined().optional(),
});
/**
 * 申請明細「新舊兼容」寫入 schema（createCase / updateCase 用）：
 * TOP（kind='TOP'）｜TAP（kind='TAP'）｜LEGACY（無 kind，舊調用方）。
 */
export const caseApplicationDetailsSchema = z.union([
    applicationDetailsTopSchema,
    applicationDetailsTapSchema,
    applicationDetailsLegacyInputSchema,
]);
/**
 * 驗證方案 → 申請明細 kind 的對應（TAP → 'TAP'；TOP / ECO / 未知 → 'TOP'）。
 * ECO 暫無獨立表樣，回落 TOP（與合約評審 checklist 的回落策略一致）。
 */
export function applicationKindForScheme(scheme) {
    return scheme === 'TAP' ? 'TAP' : 'TOP';
}
/**
 * 「追蹤／展延」類驗證服務類型判別（序165：有機驗證面積依驗證類型分展示）。
 *
 * 初次申請 / 增列查驗（含未選任何類型）→ 申請型：只填【申請驗證面積】（applyAreaHa）。
 * 追蹤查驗1/2/3 / 展延查驗 → 查驗型：分填【有機面積】+【有機轉型期面積】
 * （organicAreaHa / organicConversionAreaHa），合計由前端即時加總顯示。
 * 多選時只要命中一個查驗型類型即視為查驗型（查驗類申請通常伴隨具體查驗事由）。
 */
const TRACKING_VERIFICATION_TYPE_RE = /追蹤查驗|展延查驗/;
export function isTrackingVerificationType(verificationTypes) {
    return (verificationTypes ?? []).some((t) => TRACKING_VERIFICATION_TYPE_RE.test(t));
}
/**
 * 寫入前正規化：TAP 結構的 totalArea 一律以 rows 服務端重算
 * （「驗證總面積 = 各行申請面積自動加總」是不變式，不信任客戶端傳值）；
 * TOP / LEGACY 原樣返回。
 */
export function normalizeCaseApplicationDetails(details) {
    if (details.kind === 'TAP') {
        const tap = details;
        return { ...tap, totalArea: sumTapArea(tap.rows) };
    }
    return details;
}
/**
 * 解析 cases.application_details（jsonb 原始值）為新舊兼容的判別結構。
 *
 * - kind='TOP' / 'TAP' 且通過 schema 校驗 → 回對應新結構（zod 正規化後的物件）；
 * - 帶 kind 但校驗不過（髒資料）→ 降級歸 'LEGACY' 原樣帶回，讀取端按舊渲染兜底；
 * - 無 kind 的舊 jsonb → 'LEGACY' 原樣帶回（不經 zod 剝離，保留未知鍵）；
 * - null / undefined / 非物件 → null（呼叫端顯示「—」）。
 */
export function parseApplicationDetails(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }
    const obj = raw;
    if (obj.kind === 'TOP') {
        const parsed = applicationDetailsTopSchema.safeParse(obj);
        if (parsed.success)
            return { kind: 'TOP', details: parsed.data };
    }
    else if (obj.kind === 'TAP') {
        const parsed = applicationDetailsTapSchema.safeParse(obj);
        if (parsed.success)
            return { kind: 'TAP', details: parsed.data };
    }
    // 無 kind 的舊結構，或帶 kind 但校驗不過的髒資料：原樣歸 LEGACY
    return { kind: 'LEGACY', details: obj, raw: obj };
}
